"use client";

import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import Link from "next/link";
import { ArrowLeft, ChevronLeft, ChevronRight, List, Bookmark, Loader2 } from "lucide-react";
import { toggleBookmark, upsertReadingProgress } from "@/lib/actions/reader-actions";
import { ClaimChip } from "@/components/claim/claim-chip";
import { ChapterComments } from "@/components/reader/chapter-comments";
import type { ChapterCommentView } from "@/lib/actions/comment-actions";
import { renderNode, hasRenderableText, type TipTapNode } from "./tiptap-render";
import { ReaderTableOfContents, type ReaderChapterMeta } from "./reader-table-of-contents";
import "./reader-view.css";

// ─── Types ───────────────────────────────────────────────

interface ChapterContent {
  id: string;
  title: string;
  content: unknown;
  /** End-of-chapter author's note (TipTap JSON), if the author added one. */
  authorNote?: unknown;
  wordCount: number | null;
  sortOrder: number;
  publishedAt: Date | null;
  isLocked?: boolean;
  coinPrice?: number | null;
  freeAfter?: Date | null;
}
interface ReaderViewProps {
  storyId: string;
  storyTitle: string;
  storySlug: string;
  authorName: string;
  authorSlug?: string | null;
  isAuthorClaimed?: boolean;
  chapters: ReaderChapterMeta[];
  currentChapter: ChapterContent;
  initialBookmarked: boolean;
  showTextToSpeech?: boolean;

  // Optional preview mode overrides
  isPreview?: boolean;
  onClose?: () => void;
  onNavigateChapter?: (chapterId: string) => void;
  isAuthenticated?: boolean;
}

// The TipTap JSON → React renderer lives in ./tiptap-render and is shared with
// the public wiki page.

// ─── Component ───────────────────────────────────────────

import { unlockPremiumChapter } from "@/lib/actions/reader-coin-actions";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { Lock, Coins, ExternalLink, Play, SquarePlay, SquarePen } from "lucide-react";
import { ChapterAudioPlayer } from "./chapter-audio-player";
import { ExternalStoryCtaPrompt } from "./external-story-cta-prompt";
import type { ExternalStoryCtaConfig } from "@/lib/external-story-cta";
import type { StorySpotlightState } from "@/lib/spotlights/queries";
import { ChapterEndSpotlight } from "@/components/spotlights/chapter-end-spotlight";
import { ChapterReadTracker } from "@/components/reader/chapter-read-tracker";
interface ReaderViewInterface extends ReaderViewProps {
  isSuperReader?: boolean;
  currentUserId?: string | null;
  isStoryAuthor?: boolean;
  comments?: ChapterCommentView[];
  externalCta?: ExternalStoryCtaConfig | null;
  spotlightState?: StorySpotlightState;
}
export function ReaderView({
  storyId,
  storyTitle,
  storySlug,
  authorName,
  authorSlug,
  isAuthorClaimed = true,
  chapters,
  currentChapter,
  initialBookmarked,
  showTextToSpeech = true,
  isPreview = false,
  onClose,
  onNavigateChapter,
  isAuthenticated,
  isSuperReader = false,
  isStoryAuthor = false,
  comments,
  externalCta,
  spotlightState
}: ReaderViewInterface) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentIndex = chapters.findIndex(c => c.id === currentChapter.id);
  const currentChapterMeta = chapters[currentIndex];
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;
  const estimatedReadTime = Math.max(1, Math.round((currentChapter.wordCount ?? 0) / 200));
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [isPending, startTransition] = useTransition();
  const [isTocOpen, setIsTocOpen] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(searchParams?.get("autoplay") === "true");

  // Reset navigating state when the chapter actually changes
  useEffect(() => {
    setIsNavigating(false);
  }, [currentChapter.id]);
  const handleBookmarkToggle = () => {
    if (isPreview) return;
    startTransition(async () => {
      const result = await toggleBookmark(storyId);
      if ("error" in result) {
        if (result.error === "Not authenticated") {
          router.push("/login");
        } else {
          toast.error(result.error);
        }
        return;
      }
      setIsBookmarked(result.bookmarked);
    });
  };
  const handleChapterNav = useCallback((chapterId: string, keepAutoplay = false, completeCurrent = false) => {
    if (isPreview && onNavigateChapter) {
      onNavigateChapter(chapterId);
      return;
    }
    setIsNavigating(true);
    if (!isPreview) {
      // Fire-and-forget progress update on navigation
      upsertReadingProgress(storyId, chapterId, completeCurrent ? currentChapter.id : undefined).catch(() => {});
    }
    const query = keepAutoplay ? `?chapter=${chapterId}&autoplay=true` : `?chapter=${chapterId}`;
    router.push(`/story/${storySlug}/read${query}`);
  }, [currentChapter.id, isPreview, onNavigateChapter, storyId, storySlug, router]);
  const handleChapterEnd = useCallback(() => {
    if (isAutoPlay && nextChapter) {
      handleChapterNav(nextChapter.id, true, true);
    }
  }, [isAutoPlay, nextChapter, handleChapterNav]);
  const openToc = useCallback(() => setIsTocOpen(true), []);
  const closeToc = useCallback(() => setIsTocOpen(false), []);
  const handleUnlock = async () => {
    if (!currentChapter.id) return;
    setIsUnlocking(true);
    try {
      const res = await unlockPremiumChapter(currentChapter.id);
      if (res.error) {
        if (res.error === "Insufficient coins" || res.error === "Not authenticated") {
          router.push("/pricing");
          return;
        } else {
          toast.error(res.error);
        }
      } else {
        toast.success("Chapter unlocked successfully!");
        // Re-navigation shouldn't be strictly necessary since we called revalidatePath but it forces a refresh here
        router.refresh();
      }
    } catch {
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsUnlocking(false);
    }
  };

  // Close the ToC drawer on Escape key
  useEffect(() => {
    if (!isTocOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeToc();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isTocOpen, closeToc]);

  // Memoize the rendered TipTap content to avoid re-rendering on navigation interactions
  const renderedContent = useMemo(() => {
    return renderNode(currentChapter.content as TipTapNode, 0);
  }, [currentChapter.content]);
  const renderedAuthorNote = useMemo(() => {
    const note = currentChapter.authorNote as TipTapNode | null | undefined;
    // Guard on actual text, not just presence: an empty doc ({content:[]})
    // is truthy and would render a bare "Author's Note" frame with no body.
    if (!note || !hasRenderableText(note)) return null;
    return renderNode(note, 0);
  }, [currentChapter.authorNote]);
  return <div className="relative flex min-h-screen w-full flex-col bg-[#f6f6f8] dark:bg-[#131121] text-slate-900 dark:text-slate-100 font-sans">

                {/* ─── Header ───────────────────────────────── */}
                <header className="reader-header px-4 py-3">
                    <div className="max-w-2xl mx-auto flex items-center gap-3">
                        {isPreview ? <button onClick={onClose} className="p-2 -ml-2 text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">
                                <ArrowLeft className="size-5" />
                            </button> : <Link href="/browse" className="p-2 -ml-2 text-slate-600 dark:text-slate-400 hover:text-primary transition-colors">
                                <ArrowLeft className="size-5" />
                            </Link>}
                        <div className="flex-1 overflow-hidden">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                {storyTitle}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-2">
                                <span>
                                    by{" "}
                                    {authorSlug ? <Link href={`/${authorSlug}`} className="hover:text-primary hover:underline transition-colors">
                                            {authorName}
                                        </Link> : authorName}
                                </span>
                                {!isAuthorClaimed && authorSlug && <ClaimChip authorSlug={authorSlug} />}
                            </p>
                        </div>
                        <button onClick={handleBookmarkToggle} disabled={isPending} className={`p-2 transition-colors ${isBookmarked ? 'text-brand' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}>
                            <Bookmark className="size-5" fill={isBookmarked ? 'currentColor' : 'none'} />
                        </button>
                    </div>
                </header>

                {/* ─── Main Content ─────────────────────────── */}
                <main className={`max-w-2xl mx-auto w-full px-6 py-10 pb-28 transition-opacity duration-300 ${isNavigating ? 'opacity-40 pointer-events-none' : ''}`}>

                    {/* Loading spinner overlay during chapter navigation */}
                    {isNavigating && <div className="reader-loading-overlay">
                            <Loader2 className="size-8 text-primary animate-spin" />
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 font-medium">Loading chapter…</p>
                        </div>}

                    {/* Chapter Header */}
                    <div className="mb-12">
                        {showTextToSpeech && isSuperReader && !currentChapter.isLocked && <div className="flex justify-end mb-6">
                                <button onClick={() => setIsAutoPlay(!isAutoPlay)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all shadow-sm ${isAutoPlay ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-700"}`} aria-label="Toggle Autoplay">
                                    {isAutoPlay ? <SquarePlay className="size-3.5" /> : <Play className="size-3.5" />}
                                    Autoplay {isAutoPlay ? 'On' : 'Off'}
                                </button>
                            </div>}
                        <div className="text-center">
                            {currentChapterMeta && <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-primary">
                                    {currentChapterMeta.volumeTitle}
                                </p>}
                            <div className="flex items-center justify-center gap-2">
                                {currentChapter.isLocked && <Lock className="size-5 text-amber-500 -mt-2" />}
                                <h1 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white leading-tight font-heading">
                                    {currentChapter.title}
                                </h1>
                            </div>

                            <div className="flex justify-center gap-3 text-sm text-slate-500 dark:text-slate-400 italic">
                            <span>{estimatedReadTime} min read</span>
                            {currentChapter.publishedAt && <>
                                    <span>•</span>
                                    <span>
                                        Published{" "}
                                        {new Date(currentChapter.publishedAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                                    </span>
                                </>}
                        </div>
                    </div>
                </div>

                {/* Chapter Body or Paywall */}
                    {currentChapter.isLocked ? <div data-testid="paywall-container" className="flex flex-col items-center justify-center p-8 bg-card rounded-2xl border border-amber-500/20 my-10 max-w-lg mx-auto text-center space-y-4 shadow-xl shadow-amber-500/5 bg-gradient-to-b from-white to-amber-50/50 dark:from-slate-900 dark:to-amber-950/20">
                            <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full mb-2">
                                <Lock className="size-10 text-amber-500" />
                            </div>
                            <h2 className="text-xl font-bold font-heading text-foreground">
                                Premium Chapter
                            </h2>
                            <p className="text-muted-foreground text-sm max-w-sm">
                                Support the author by unlocking this premium chapter for <strong>{currentChapter.coinPrice} coins</strong>.
                            </p>
                            {currentChapter.freeAfter && <p className="text-sm text-muted-foreground" suppressHydrationWarning>Or read it free after {new Date(currentChapter.freeAfter).toLocaleString()}.</p>}
                            <button onClick={handleUnlock} disabled={isUnlocking} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 rounded-xl py-3.5 mt-8 font-semibold transition-opacity shadow-md shadow-amber-500/20 disabled:opacity-50">
                                <Coins className="size-5" />
                                {isUnlocking ? "Unlocking..." : `Unlock for ${currentChapter.coinPrice} Coins`}
                            </button>
                        </div> : <>
                            <article className="reader-content rich-content text-slate-800 dark:text-slate-200">
                                {renderedContent}
                            </article>

                            {/* ─── Author's Note ─────────────────────── */}
                            {renderedAuthorNote && <aside data-testid="author-note" className="mt-12 rounded-xl border border-primary/15 bg-secondary/60 dark:bg-secondary/40 p-5">
                                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
                                        <SquarePen className="size-3.5" />
                                        Author&apos;s Note
                                    </div>
                                    {/* Deliberately NOT reader-content: the note is
                                        the author talking, not the narrator. */}
                                    <div className="rich-content font-sans text-[0.95rem] leading-relaxed text-slate-700 dark:text-slate-300">
                                        {renderedAuthorNote}
                                    </div>
                                </aside>}

                            {!isPreview && <ChapterReadTracker storyId={storyId} chapterId={currentChapter.id} chapterPosition={currentChapter.sortOrder} isAuthenticated={!!isAuthenticated} isOwnStory={isStoryAuthor} />}

                            {!isPreview && spotlightState && <ChapterEndSpotlight storyId={storyId} chapterId={currentChapter.id} initialState={spotlightState} isAuthenticated={!!isAuthenticated} />}

                            {!nextChapter && externalCta && <ExternalStoryCtaPrompt cta={externalCta} />}

                            {/* ─── End of Chapter CTA ────────────────── */}
                            <section className="mt-20 pt-10 border-t border-slate-200 dark:border-slate-800/60 text-center">
                                <h3 className="text-xl font-bold mb-2 font-heading">Enjoyed the chapter?</h3>
                                <p className="text-slate-500 dark:text-slate-400 mb-6">
                                    Let the author know your thoughts below!
                                </p>
                            </section>

                            {/* ─── Comments ──────────────────────────── */}
                            {!isPreview && <ChapterComments key={currentChapter.id} chapterId={currentChapter.id} initialComments={comments ?? []} isAuthenticated={!!isAuthenticated} isStoryAuthor={isStoryAuthor} />}

                            {/* ─── Unclaimed-author claim CTA ───────── */}
                            {!isAuthorClaimed && authorSlug && <section className="mt-8">
                                    <ClaimChip authorSlug={authorSlug} variant="banner" />
                                </section>}
                        </>}
                </main>

                {/* ─── Sticky Bottom Navigation ─────────────── */}
                <nav className="reader-bottom-nav" data-testid="reader-bottom-nav">
                    {/* Audio Player now docks perfectly on top of this sticky footer */}
                    {showTextToSpeech && !currentChapter.isLocked && <ChapterAudioPlayer storySlug={storySlug} chapterId={currentChapter.id} nextChapterId={nextChapter?.id} isAuthenticated={isAuthenticated} autoPlay={isAutoPlay} onChapterEnd={handleChapterEnd} />}
                    <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
                        {prevChapter ? isPreview ? <button onClick={() => handleChapterNav(prevChapter.id)} disabled={isNavigating} className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-slate-200/80 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:pointer-events-none">
                                    <ChevronLeft className="size-5" />
                                    Previous
                                </button> : <Link href={`/story/${storySlug}/read?chapter=${prevChapter.id}`} onClick={e_0 => {
          e_0.preventDefault();
          if (isNavigating) return;
          handleChapterNav(prevChapter.id);
        }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-slate-200/80 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors ${isNavigating ? 'opacity-50 pointer-events-none' : ''}`} aria-disabled={isNavigating}>
                                    <ChevronLeft className="size-5" />
                                    Previous
                                </Link> : <div className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-slate-200/40 dark:bg-slate-800/20 text-slate-400 dark:text-slate-600 font-medium cursor-not-allowed">
                                <ChevronLeft className="size-5" />
                                Previous
                            </div>}

                        <div className="flex gap-2">
                            <button onClick={openToc} aria-label="Table of contents" className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-primary transition-colors">
                                <List className="size-5" />
                            </button>
                        </div>

                        {nextChapter ? isPreview ? <button onClick={() => handleChapterNav(nextChapter.id, false, true)} disabled={isNavigating} className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:pointer-events-none">
                                    Next Chapter
                                    <ChevronRight className="size-5" />
                                </button> : <Link href={`/story/${storySlug}/read?chapter=${nextChapter.id}`} onClick={e_1 => {
          e_1.preventDefault();
          if (isNavigating) return;
          handleChapterNav(nextChapter.id, false, true);
        }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary text-white font-medium hover:opacity-90 transition-opacity ${isNavigating ? 'opacity-50 pointer-events-none' : ''}`} aria-disabled={isNavigating}>
                                    Next Chapter
                                    <ChevronRight className="size-5" />
                                </Link> : externalCta && !currentChapter.isLocked ? <a href={externalCta.url} target="_blank" rel="noopener noreferrer nofollow" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 font-medium text-white transition-opacity hover:opacity-90">
                                {externalCta.buttonLabel}
                                <ExternalLink className="size-4" />
                            </a> : <div className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-primary/30 text-primary/50 font-medium cursor-not-allowed">
                                Next Chapter
                                <ChevronRight className="size-5" />
                            </div>}
                    </div>
                </nav>

                {/* ─── Table of Contents Drawer ─────────────── */}
                {isTocOpen && <ReaderTableOfContents chapters={chapters} currentChapterId={currentChapter.id} storySlug={storySlug} isPreview={isPreview} isNavigating={isNavigating} onClose={closeToc} onNavigate={handleChapterNav} />}
            </div>;
}